import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { X, Mail, Edit, Check, Send, Link2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { MysteryCharacter } from "@/interfaces/mystery";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { trackCharacterAssignment } from "@/lib/analytics";

import i18n from "@/i18n";
interface CharacterAssignment {
  id?: string;
  character_id: string;
  guest_name: string;
  guest_email: string;
  is_sent: boolean;
  sent_at?: string;
  access_token?: string;
  short_code?: string;
}

interface MysteryGuestManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  characters: MysteryCharacter[];
  mysteryId: string;
  mysteryTitle?: string;
  packageId?: string;
}

const MysteryGuestManager: React.FC<MysteryGuestManagerProps> = ({
  open,
  onOpenChange,
  characters,
  mysteryId,
  mysteryTitle = "Your Mystery",
  packageId
}) => {
  const { t, i18n } = useTranslation();
  const [assignments, setAssignments] = useState<CharacterAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [hostEmail, setHostEmail] = useState("");
  const [hostEmailSent, setHostEmailSent] = useState(false);
  const [hostEmailSending, setHostEmailSending] = useState(false);

  // Initialize assignments when dialog opens
  useEffect(() => {
    if (open && characters.length > 0) {
      loadExistingAssignments();
    }
    if (open && packageId) {
      loadHostEmailStatus();
    }
  }, [open, characters, mysteryId, packageId]);

  const loadHostEmailStatus = async () => {
    if (!packageId) return;
    try {
      const { data, error } = await supabase
        .from('mystery_packages')
        .select('host_email, host_access_token')
        .eq('id', packageId)
        .single();

      if (error) throw error;

      if (data?.host_email) {
        setHostEmail(data.host_email);
        if (data.host_access_token) {
          setHostEmailSent(true);
        }
      }
    } catch (error) {
      console.error('Error loading host email status:', error);
    }
  };

  const sendHostKit = async () => {
    if (!packageId) return;
    if (!hostEmail.trim() || !isValidEmail(hostEmail)) {
      toast.error(t('character.guestManager.hostKit.toasts.invalidEmail'));
      return;
    }

    setHostEmailSending(true);
    try {
      // Ensure host_access_token exists and save email
      const { data: packageData, error: fetchError } = await supabase
        .from('mystery_packages')
        .select('host_access_token')
        .eq('id', packageId)
        .single();

      if (fetchError) throw fetchError;

      let accessToken = packageData?.host_access_token;

      // Update host_email (token is auto-generated by DB default)
      const { data: updated, error: updateError } = await supabase
        .from('mystery_packages')
        .update({ host_email: hostEmail.trim() })
        .eq('id', packageId)
        .select('host_access_token')
        .single();

      if (updateError) throw updateError;
      accessToken = updated.host_access_token;

      // Call the Edge Function
      const { data: emailResponse, error: emailError } = await supabase.functions.invoke('send-host-email', {
        body: {
          host_email: hostEmail.trim(),
          mystery_title: mysteryTitle,
          access_token: accessToken,
          language: i18n.language,
        }
      });

      if (emailError) throw new Error(`Failed to send email: ${emailError.message}`);
      if (emailResponse && !emailResponse.success) throw new Error(emailResponse.error || 'Failed to send email');

      setHostEmailSent(true);
      toast.success(t('character.guestManager.hostKit.toasts.success'));
    } catch (error) {
      console.error('Error sending host kit:', error);
      toast.error(t('character.guestManager.hostKit.toasts.error'));
    } finally {
      setHostEmailSending(false);
    }
  };

  const loadExistingAssignments = async () => {
    try {
      const { data, error } = await supabase
        .from('character_assignments')
        .select('*')
        .eq('mystery_id', mysteryId);

      if (error) throw error;

      // Create assignments array with existing data or empty defaults
      const assignmentMap = new Map(data?.map(a => [a.character_id, a]) || []);
      
      const newAssignments = characters.map(character => {
        const existing = assignmentMap.get(character.id);
        return existing ? {
          id: existing.id,
          character_id: character.id,
          guest_name: existing.guest_name,
          guest_email: existing.guest_email,
          is_sent: existing.is_sent,
          sent_at: existing.sent_at,
          access_token: existing.access_token,
          short_code: existing.short_code
        } : {
          character_id: character.id,
          guest_name: '',
          guest_email: '',
          is_sent: false
        };
      });

      setAssignments(newAssignments);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast.error(i18n.t("toastsUi.mystery.loadAssignmentsFailed"));
    }
  };

  const updateAssignment = (characterId: string, field: string, value: string) => {
    setAssignments(prev => prev.map(assignment => 
      assignment.character_id === characterId 
        ? { ...assignment, [field]: value }
        : assignment
    ));
  };

  const isValidEmail = (email: string) => {
    return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
  };

  // Email path requires a valid email. Copy-link path only needs a name (email optional).
  const canSend = (assignment: CharacterAssignment) => {
    return assignment.guest_name.trim() &&
           assignment.guest_email.trim() &&
           isValidEmail(assignment.guest_email) &&
           !assignment.is_sent;
  };

  const canShare = (assignment: CharacterAssignment) => {
    return !!assignment.guest_name.trim() && !assignment.is_sent;
  };

  // Short-code links (ADR-0031): mysterymaker.party/c/<8-char code>, far shorter
  // than the raw UUID. CharacterAccess resolves the code back to the token.
  const buildShareUrl = (code: string) => `${window.location.origin}/c/${code}`;

  // Insert or update the assignment row and mark it sent. Returns the saved row
  // (which carries the DB-generated access_token). Shared by the email and copy-link paths.
  const persistAssignment = async (assignment: CharacterAssignment) => {
    const assignmentData = {
      mystery_id: mysteryId,
      character_id: assignment.character_id,
      guest_name: assignment.guest_name.trim(),
      guest_email: assignment.guest_email.trim(), // NOT NULL column; '' when copy-link only
      is_sent: true,
      sent_at: new Date().toISOString()
    };

    const result = assignment.id
      ? await supabase.from('character_assignments').update(assignmentData).eq('id', assignment.id).select().single()
      : await supabase.from('character_assignments').insert(assignmentData).select().single();

    if (result.error) throw result.error;
    return result.data;
  };

  const applyPersisted = (characterId: string, row: { id: string; sent_at: string; access_token: string; short_code: string }) => {
    setAssignments(prev => prev.map(a =>
      a.character_id === characterId
        ? { ...a, id: row.id, is_sent: true, sent_at: row.sent_at, access_token: row.access_token, short_code: row.short_code }
        : a
    ));
  };

  // Clipboard write with a legacy fallback (Safari can block writeText after an await).
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (e) {
      // fall through to legacy path
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (e) {
      return false;
    }
  };

  // Synchronous re-entry guard: the button's disabled={loading} only takes
  // effect after a re-render, so a fast double-click fires this handler twice
  // and sends the guest's character (and email) twice. A ref blocks the second
  // call in the same tick. Sequential callers (sendAllAssignments) are fine —
  // each await completes (clearing the ref in finally) before the next starts.
  const sendInFlightRef = useRef(false);

  const sendCharacterAssignment = async (assignment: CharacterAssignment) => {
    if (!canSend(assignment)) return;
    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;

    setLoading(true);
    try {
      const character = characters.find(c => c.id === assignment.character_id);
      if (!character) throw new Error('Character not found');

      const row = await persistAssignment(assignment);
      applyPersisted(assignment.character_id, row);

      // Call the Edge Function to send the email
      const { error: emailError } = await supabase.functions.invoke('send-character-email', {
        body: {
          guest_email: assignment.guest_email.trim(),
          guest_name: assignment.guest_name.trim(),
          character_name: character.character_name,
          character_details: character.description?.substring(0, 200) + '...' || 'Mystery character details...',
          access_token: row.access_token || 'temp-token',
          mystery_title: mysteryTitle,
          language: i18n.language,
        }
      });

      if (emailError) {
        throw new Error(`Failed to send email: ${emailError.message}`);
      }
      toast.success(t('character.guestManager.toasts.characterSent', { name: assignment.guest_name }));

      // Track character assignment for analytics
      trackCharacterAssignment(mysteryId, characters.length);

    } catch (error) {
      console.error('Error sending assignment:', error);
      toast.error(t('character.guestManager.toasts.sendFailed'));
    } finally {
      sendInFlightRef.current = false;
      setLoading(false);
    }
  };

  // Copy this guest's personal access link to the clipboard so the host can share it
  // themselves (WhatsApp, etc.). Mints the row/token if needed and locks the name in,
  // exactly like the email path (copying counts as sending — see ADR-0030).
  const copyShareLink = async (assignment: CharacterAssignment) => {
    if (!assignment.guest_name.trim()) {
      toast.error(t('character.guestManager.toasts.nameRequired'));
      return;
    }

    setLoading(true);
    try {
      let code = assignment.short_code;
      // Create the row (or re-lock a not-yet-sent one) to guarantee a code exists.
      if (!assignment.id || !assignment.is_sent || !code) {
        const row = await persistAssignment(assignment);
        applyPersisted(assignment.character_id, row);
        code = row.short_code;
      }

      const url = buildShareUrl(code);
      const copied = await copyToClipboard(url);
      if (copied) {
        toast.success(t('character.guestManager.toasts.linkCopied', { name: assignment.guest_name.trim() }));
      } else {
        toast.message(t('character.guestManager.toasts.copyManual'), { description: url, duration: 12000 });
      }

      trackCharacterAssignment(mysteryId, characters.length);
    } catch (error) {
      console.error('Error copying share link:', error);
      toast.error(t('character.guestManager.toasts.copyFailed'));
    } finally {
      setLoading(false);
    }
  };

  const editAssignment = async (assignment: CharacterAssignment) => {
    if (!assignment.id) return;

    try {
      // Update database to mark as not sent
      const { error } = await supabase
        .from('character_assignments')
        .update({ is_sent: false, sent_at: null })
        .eq('id', assignment.id);

      if (error) throw error;

      // Update local state
      setAssignments(prev => prev.map(a => 
        a.character_id === assignment.character_id 
          ? { ...a, is_sent: false, sent_at: undefined }
          : a
      ));

    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error(t('character.guestManager.toasts.updateFailed'));
    }
  };

  const sendAllAssignments = async () => {
    const validAssignments = assignments.filter(a => canSend(a));
    if (validAssignments.length === 0 && !(!hostEmailSent && hostEmail.trim() && isValidEmail(hostEmail))) {
      toast.error(t('character.guestManager.toasts.noValidAssignments'));
      return;
    }

    setLoading(true);
    try {
      // Send host kit if email is filled but not yet sent
      if (packageId && hostEmail.trim() && isValidEmail(hostEmail) && !hostEmailSent) {
        await sendHostKit();
      }

      for (const assignment of validAssignments) {
        await sendCharacterAssignment(assignment);
      }
      if (validAssignments.length > 0) {
        toast.success(t('character.guestManager.toasts.allSent', { count: validAssignments.length }));
      }
    } catch (error) {
      toast.error(t('character.guestManager.toasts.sendFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to clean character description
  const cleanCharacterDescription = (description: string): string => {
    if (!description) return '';
    
    // Remove "## CHARACTER DESCRIPTION" prefix if present
    const cleanedDescription = description.replace(/^##\s*CHARACTER\s*DESCRIPTION\s*/i, '').trim();
    
    return cleanedDescription;
  };

  const assignedCount = assignments.filter(a => a.guest_name.trim() && a.guest_email.trim()).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('character.guestManager.title')}</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground mb-4">
          {t('character.guestManager.subtitle')}
        </p>
        <div className="flex-1 overflow-y-auto pr-2 -mr-2">
          {/* Host Kit Section */}
          {packageId && (
            <div className="mb-6 p-4 border rounded-lg bg-muted/30">
              <h3 className="font-semibold mb-1">{t('character.guestManager.hostKit.title')}</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t('character.guestManager.hostKit.subtitle')}
              </p>
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder={t('character.guestManager.hostKit.emailPlaceholder')}
                  value={hostEmail}
                  onChange={(e) => { setHostEmail(e.target.value); setHostEmailSent(false); }}
                  disabled={hostEmailSending}
                  className="max-w-xs"
                />
                {hostEmailSent ? (
                  <div className="flex items-center text-green-600 text-sm gap-1">
                    <Check className="h-4 w-4" />
                    {t('character.guestManager.hostKit.sent')}
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={sendHostKit}
                    disabled={hostEmailSending || !hostEmail.trim()}
                  >
                    {hostEmailSending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        {t('character.guestManager.hostKit.sending')}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-1" />
                        {t('character.guestManager.hostKit.sendButton')}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('character.guestManager.table.character')}</TableHead>
                <TableHead>{t('character.guestManager.table.guestName')}</TableHead>
                <TableHead>{t('character.guestManager.table.email')}</TableHead>
                <TableHead>{t('character.guestManager.table.action')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((assignment) => {
                const character = characters.find(c => c.id === assignment.character_id);
                if (!character) return null;

                const isLocked = assignment.is_sent;
                const canSendThis = canSend(assignment);

                // Clean the character description
                const cleanedDescription = cleanCharacterDescription(character.description || '');
                const truncatedDescription = cleanedDescription.length > 60 
                  ? `${cleanedDescription.substring(0, 60)}...`
                  : cleanedDescription;

                return (
                  <TableRow key={character.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{character.character_name}</div>
                        <div className="text-xs text-muted-foreground line-clamp-2">
                          {truncatedDescription}
                        </div>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <Input
                        placeholder={t('character.guestManager.placeholders.guestName')}
                        value={assignment.guest_name}
                        onChange={(e) => updateAssignment(assignment.character_id, 'guest_name', e.target.value)}
                        disabled={assignment.is_sent}
                      />
                    </TableCell>

                    <TableCell>
                      <Input
                        type="email"
                        placeholder={t('character.guestManager.placeholders.email')}
                        value={assignment.guest_email}
                        onChange={(e) => updateAssignment(assignment.character_id, 'guest_email', e.target.value)}
                        disabled={assignment.is_sent}
                      />
                    </TableCell>

                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        {isLocked ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyShareLink(assignment)}
                              disabled={loading}
                            >
                              <Link2 className="h-4 w-4 mr-1" />
                              {t('character.guestManager.buttons.copyLink')}
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => editAssignment(assignment)}
                              disabled={loading}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              {t('character.guestManager.buttons.edit')}
                            </Button>
                            <div className="flex items-center text-green-600 text-xs">
                              <Check className="h-3 w-3 mr-1" />
                              {t('character.guestManager.status.sent')}
                            </div>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyShareLink(assignment)}
                              disabled={!canShare(assignment) || loading}
                            >
                              <Link2 className="h-4 w-4 mr-1" />
                              {t('character.guestManager.buttons.copyLink')}
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() => sendCharacterAssignment(assignment)}
                              disabled={!canSendThis || loading}
                            >
                              <Mail className="h-4 w-4 mr-1" />
                              {t('character.guestManager.buttons.send')}
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="border-t pt-4 flex items-center justify-between">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onOpenChange(false)}
            >
              {t('character.guestManager.buttons.close')}
            </Button>
            <Button
              onClick={sendAllAssignments}
              disabled={loading || !assignments.some(canSend)}
            >
              {t('character.guestManager.buttons.sendAll')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MysteryGuestManager;
